import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Column,
  ColumnChooser,
  ColumnFixing,
  DataGrid,
  Editing,
  Export,
  FilterBuilderPopup,
  FilterRow,
  HeaderFilter,
  Item,
  MasterDetail,
  Pager,
  Paging,
  RowDragging,
  SearchPanel,
  Sorting,
  StateStoring,
  Summary,
  Toolbar,
} from 'devextreme-react/data-grid';
import { ClassNameProps, ColumnInfo } from '@/Types';
import { DefaultOptions, DragOptions, EditOptions, ExportOptions, FilterOptions, SelectOptions } from './options';
import { useRerenderOnChangeLocale } from './hooks/useRerenderOnChangeLocale';
import { useOnCellPrepared } from './hooks/useOnCellPrepared';
import { useOnContentReady } from './hooks/useOnContentReady';
import { useSelectionChanged } from './hooks/useSelectionChanged';
import { useOnReorder } from './hooks/useOnReorder';
import { useOnSaving } from './hooks/useOnSaving';
import { useOnExporting } from './hooks/useOnExporting';
import { FilterBuilderButton } from './modules/FilterBuilderButton/FilterBuilderButton';
import { ClearFiltersButton } from './modules/ClearFiltersButton/ClearFiltersButton';
import { TableSearchButton } from './modules/TableSearchButton/TableSearchButton';
import cn from 'classnames';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useSummaryItems } from './hooks/useSummaryItems';
import { useColumns } from './hooks/useColumns';
import styles from './DataTable.scss';
import { useStateStoring } from './hooks/useStateStoring';
import { useOnFilterChanged } from './hooks/useOnFilterChanged';
import { CustomizationOptions } from './options/CustomizationOptions';
import { useOnRowPrepared } from './hooks/useOnRowPrepared';
import { DetailViewOptions } from './options/DetailViewOptions';
import { useOnRowsExpand } from '@/Components/DataTable/hooks/useOnRowsExpand';
import CustomStore from 'devextreme/data/custom_store';
import { useOnOptionChanged } from './hooks/useOnOptionChanged';
import { PaginationOptions } from './options/PaginationOptions';
import { TableExportButton } from './modules/TableExportButton/TableExportButton';
import { ColumnChooseButton } from './modules/ColumnChooseButton/ColumnChooseButton';
import { TableImportButton } from './modules/TableImportButton/TableImportButton';
import { calculateSummary } from './services/utils';

export type DataTableProps<TData extends object> = {
  gridRef?: React.MutableRefObject<DataGrid<TData, keyof TData> | undefined>;

  /*
    Required!
    */
  rowKey: keyof TData | '';
  rows: TData[];
  selectedRowKeys?: (string | number)[];
  mergeColumnsMap?: Partial<{ [K in keyof TData]: boolean }>;
  columnInfos?: ColumnInfo<any>[];
  stateStoringKey?: string;

  /*
    Options
  */
  defaultOptions?: DefaultOptions;
  selectOptions?: SelectOptions<TData>;
  editOptions?: EditOptions<TData>;
  dragOptions?: DragOptions<TData>;
  filterOptions?: FilterOptions<TData>;
  exportOptions?: ExportOptions;
  customizationOptions?: CustomizationOptions<TData>;
  detailViewOptions?: DetailViewOptions<TData>;
  paginationOptions?: PaginationOptions;
} & ClassNameProps;

function DataTableFunction<TData extends object>(props: DataTableProps<TData>) {
  const {
    gridRef,
    rowKey,
    rows,
    selectedRowKeys,
    mergeColumnsMap,
    columnInfos,
    stateStoringKey,
    className,

    defaultOptions: {
      height,
      allowColumnReordering = true,
      allowColumnResizing = true,
      columnAutoWidth = true,
      showBorders = true,
      showRowLines = true,
      showPagination = true,
      showPageSizeSelector = true,
      toolbarPosition = 'after',
    } = {},

    paginationOptions: { allowRemotePagination = false, itemsTotalCount, onPageIndexChange, onPageSizeChange } = {},

    selectOptions: {
      selectMode = 'multiple',
      selectAllMode = 'allPages',
      onFocusedRowChanged,
      onSelectedRowsChanged,
      onRowDblClick,
      showCheckBoxesMode = 'none',
    } = {},

    editOptions: { needEditing, showActionColumn, mode = 'row', onEditIconClick, onSave } = {},

    dragOptions: {
      showDragIcons,
      onDragEnd,
      onTableReorder,
      dragGroup,
      dragBetweenTablesId,
      dragRender,
      onDragStart,
      onDragMove,
    } = {},

    filterOptions: {
      needGlobalSearchPanel = true,
      needColumnChooser,
      needSearchFilterRow,
      needHeaderFilter = true,
      needSorting = true,
      needClearFiltersButton = true,
      needShowFiltersButton = true,
      showFilterBuilder = true,
      disableFilterBuilder = false,
      searchVisibleColumnsOnly = true,
      sortHighlightExcludeKey,
      onColumnSort,
      onSearchItem,
    } = {},

    exportOptions: {
      needExport = true,
      exportFileName = 'ship-cheaper',
      exportWorksheetName,
      exportSelectedData = true,
      onExport,
    } = {},

    customizationOptions: { customizeRow } = {},

    detailViewOptions: {
      expandRows = false,
      onRowCollapsed,
      onRowExpanded,
      onRowCollapsing,
      onRowExpanding,
      detailView,
    } = {},
  } = props;

  const store = new CustomStore<TData>({
    key: rowKey.toString(),
    load: () => {
      let summary: number[] = [];

      if (columnInfos && allowRemotePagination) {
        summary = calculateSummary(rows, columnInfos);
      }

      return Promise.resolve({
        data: rows,
        totalCount: itemsTotalCount ?? rows.length,
        summary,
      });
    },
  });

  // Table ref
  const dataGridRef = useRef<DataGrid<TData, keyof TData>>();
  const innerGridRef = gridRef ?? dataGridRef;
  const gridInstance = innerGridRef.current?.instance;

  // Table states
  const [isFilterRowVisible, setIsFilterRowVisible] = useState(needSearchFilterRow);
  const [isFilterBuilderPopupVisible, setIsFilterBuilderPopupVisible] = useState(false);

  // Table hooks
  const summaryItems = useSummaryItems(columnInfos);
  const onRowsExpand = useOnRowsExpand(innerGridRef, expandRows);
  const columns = useColumns(rows, columnInfos);
  const onFilterChanged = useOnFilterChanged(innerGridRef, sortHighlightExcludeKey);

  const { onSaving } = useOnSaving(onSave);
  const { onReorder } = useOnReorder(rows, onTableReorder);
  const { onRowPrepared } = useOnRowPrepared<TData>(customizeRow);
  const { onArrowDownClick, onArrowUpClick } = useKeyboardNavigation();
  const { saveState, loadState } = useStateStoring<TData>(stateStoringKey, innerGridRef);
  const { onCellPrepared } = useOnCellPrepared(rowKey, mergeColumnsMap);
  const { shouldRerender, resetShouldRerender } = useRerenderOnChangeLocale();
  const { onExporting } = useOnExporting<TData>(innerGridRef, exportFileName, exportWorksheetName, onExport);
  const { onContentReady } = useOnContentReady(onFocusedRowChanged, onSelectedRowsChanged, selectedRowKeys);

  const { onRowClick, selectionChanged } = useSelectionChanged(
    rowKey,
    innerGridRef,
    onSelectedRowsChanged,
    onFocusedRowChanged,
  );

  const onOptionChanged = useOnOptionChanged<TData>(
    onRowsExpand,
    onPageIndexChange,
    onPageSizeChange,
    onSearchItem,
    onColumnSort,
  );

  useEffect(() => {
    onRowsExpand();
  }, [gridInstance, rows, expandRows]);

  if (shouldRerender) {
    resetShouldRerender();
    return null;
  }

  return (
    <div className={styles.tableWrapper}>
      <DataGrid
        className={cn(className, styles.table)}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={innerGridRef}
        width={'0'}
        id="dataGrid"
        height={height}
        scrolling={{
          useNative: true,
        }}
        showBorders={showBorders}
        columnAutoWidth={columnAutoWidth}
        onCellPrepared={onCellPrepared}
        dataSource={store}
        remoteOperations={{
          groupPaging: allowRemotePagination,
          filtering: false,
        }}
        columnResizingMode={'widget'}
        onOptionChanged={(e) => {
          onFilterChanged(e);
          onOptionChanged(e);
        }}
        onEditorPreparing={(e) => {
          if (e.parentType === 'searchPanel') {
            e.editorOptions.onKeyUp = (e: any) => {
              const text = e.component.option('value');
              e.component.option('value', text.trim());
            };
          }
        }}
        allowColumnResizing={allowColumnResizing}
        allowColumnReordering={allowColumnReordering}
        showRowLines={showRowLines}
        onSelectionChanged={selectionChanged}
        onKeyDown={(ev) => {
          switch (ev.event?.code) {
            case 'ArrowDown':
              ev.event?.preventDefault();
              onArrowDownClick(rowKey, innerGridRef);
              break;

            case 'ArrowUp':
              ev.event?.preventDefault();
              onArrowUpClick(innerGridRef);
              break;

            default:
              return;
          }
        }}
        onContentReady={onContentReady}
        selection={{
          mode: selectMode,
          selectAllMode: selectAllMode,
          showCheckBoxesMode: showCheckBoxesMode,
        }}
        onRowClick={onRowClick}
        onRowDblClick={(e) => {
          onRowDblClick?.(e.data);
        }}
        onSaving={onSaving}
        onRowPrepared={onRowPrepared}
        onRowCollapsed={(e) => {
          onRowCollapsed?.(e.key);
        }}
        onRowExpanded={(e) => {
          onRowExpanded?.(e.key);
        }}
        onRowCollapsing={(e) => {
          onRowCollapsing?.(e.key);
        }}
        onRowExpanding={(e) => {
          onRowExpanding?.(e.key);
        }}
      >
        {/* Detail view */}
        {detailView && <MasterDetail component={({ data }) => detailView(data.data)} enabled={true} />}

        {/* Column filters */}
        <ColumnFixing enabled={!detailView} />
        {needSorting && <Sorting mode="multiple" showSortIndexes={true} />}
        {needHeaderFilter && <HeaderFilter visible={true} />}

        {/* Toolbar */}
        {needGlobalSearchPanel && <SearchPanel visible={true} searchVisibleColumnsOnly={searchVisibleColumnsOnly} />}
        {needExport && <Export enabled={needExport} allowExportSelectedData={exportSelectedData} />}
        <FilterRow visible={isFilterRowVisible} showOperationChooser={true} />
        <FilterBuilderPopup
          visible={isFilterBuilderPopupVisible}
          onHiding={() => setIsFilterBuilderPopupVisible(false)}
        />
        {needColumnChooser && <ColumnChooser enabled={true} mode="select" />}

        <Toolbar>
          {needGlobalSearchPanel && <Item name={'searchPanel'} location={toolbarPosition} />}

          {showFilterBuilder && (
            <Item location={toolbarPosition}>
              <FilterBuilderButton
                onClick={() => setIsFilterBuilderPopupVisible(true)}
                disabled={disableFilterBuilder}
              />
            </Item>
          )}

          {needClearFiltersButton && (
            <Item location={toolbarPosition}>
              <ClearFiltersButton innerGridRef={innerGridRef} />
            </Item>
          )}

          {needShowFiltersButton && (
            <Item location={toolbarPosition}>
              <TableSearchButton onClick={() => setIsFilterRowVisible((prev) => !prev)} />
            </Item>
          )}

          {needExport && (
            <Item location={toolbarPosition}>
              <TableImportButton />
            </Item>
          )}

          {needExport && (
            <Item location={toolbarPosition}>
              <TableExportButton onExport={onExporting} />
            </Item>
          )}

          {needColumnChooser && (
            <Item location={toolbarPosition}>
              <ColumnChooseButton innerGridRef={innerGridRef} />
            </Item>
          )}
        </Toolbar>

        {/* Pagination */}
        {showPagination && <Paging defaultPageSize={50} />}
        {showPagination && (
          <Pager
            visible={true}
            showInfo={true}
            showPageSizeSelector={showPageSizeSelector}
            allowedPageSizes={[50, 100, 200]}
          />
        )}

        {/* Drag and drop */}
        {(onDragEnd || onTableReorder) && (
          <RowDragging
            group={dragGroup}
            data={dragBetweenTablesId}
            allowReordering
            dropFeedbackMode="push"
            onReorder={onReorder}
            onDragEnd={(e: any) => {
              setTimeout(() => {
                onDragEnd?.(e);
              }, 500);
            }}
            onDragStart={onDragStart}
            onDragMove={onDragMove}
            showDragIcons={showDragIcons}
            dragRender={dragRender}
          />
        )}

        {/* State storing */}
        {stateStoringKey && (
          <StateStoring enabled={true} customSave={saveState} customLoad={loadState} type="custom" savingTimeout={10} />
        )}

        {/* Editing */}
        <Editing useIcons={true} allowUpdating={needEditing} mode={mode} />
        {showActionColumn && (
          <Column caption={'Action'} type="buttons" fixed={true} fixedPosition={'left'}>
            <Button name={'edit'} onClick={onEditIconClick} />
          </Column>
        )}

        {columns}

        <Summary>{summaryItems}</Summary>
      </DataGrid>
    </div>
  );
}

export const DataTable = React.memo(DataTableFunction) as typeof DataTableFunction;
